import { ResoResource, ResourceFields } from './reso';

// Basic type definitions
type FilterOperator =
  | 'eq'
  | 'ne'
  | 'contains'
  | 'startswith'
  | 'gt'
  | 'lt'
  | 'ge'
  | 'le';
type SortDirection = 'asc' | 'desc';

// Represents a field that will be renamed in the output
interface FieldProjection<T extends ResoResource> {
  field: ResourceFields[T];
  as: string;
}

// Helper type that converts our field selections into a return type
// If a field is projected (renamed), use the new name, otherwise use the original field name
type ProjectedType<
  T extends ResoResource,
  P extends Array<FieldProjection<T> | ResourceFields[T]>,
> = {
  [K in P[number] extends FieldProjection<T>
    ? P[number]['as']
    : P[number] extends ResourceFields[T]
      ? P[number]
      : never]: any;
};

// The main options interface that defines what can be included in a query
interface QueryOptions<T extends ResoResource> {
  resource: T;
  fields?: Array<FieldProjection<T> | ResourceFields[T]>;
  filter?: Array<{
    field: ResourceFields[T];
    operator: FilterOperator;
    value: any;
  }>;
  top?: number;
  skip?: number;
  orderBy?: Array<{ field: ResourceFields[T]; direction: SortDirection }>;
  count?: boolean;
}

export class ResoQueryBuilder<T extends ResoResource> {
  private options: QueryOptions<T>;
  private selectedFields?: Array<FieldProjection<T> | ResourceFields[T]>;

  constructor(resource: T) {
    this.options = { resource };
  }

  // Static helper to create field projections with a nicer syntax
  static as<T extends ResoResource>(
    field: ResourceFields[T],
    alias: string,
  ): FieldProjection<T> {
    return { field, as: alias };
  }

  // Select which fields to return and how to name them
  select(fields: Array<FieldProjection<T> | ResourceFields[T]>) {
    this.options.fields = fields;
    this.selectedFields = fields;
    return this;
  }

  // Add a filter condition
  filter(
    field: ResourceFields[T],
    value: any,
    operator: FilterOperator = 'eq',
  ) {
    if (!this.options.filter) {
      this.options.filter = [];
    }
    this.options.filter.push({ field, operator, value });
    return this;
  }

  // Helper for CONTAINS filter
  contains(field: ResourceFields[T], value: string) {
    return this.filter(field, value, 'contains');
  }

  // Helper for STARTSWITH filter
  startsWith(field: ResourceFields[T], value: string) {
    return this.filter(field, value, 'startswith');
  }

  // Set maximum number of results
  top(limit: number) {
    this.options.top = limit;
    return this;
  }

  // Set number of results to skip (for pagination)
  skip(offset: number) {
    this.options.skip = offset;
    return this;
  }

  // Add ordering
  orderBy(field: ResourceFields[T], direction: SortDirection = 'asc') {
    if (!this.options.orderBy) {
      this.options.orderBy = [];
    }
    this.options.orderBy.push({ field, direction });
    return this;
  }

  // Request total count in response
  withCount() {
    this.options.count = true;
    return this;
  }

  // Get the current query options
  getOptions(): QueryOptions<T> {
    return this.options;
  }

  // Execute the query and return properly typed results
  async execute<R = ProjectedType<T, NonNullable<any>>>(
    executeQuery: (builder: ResoQueryBuilder<T>) => Promise<any>,
  ): Promise<{
    value: R[];
    count?: number;
    nextLink?: string;
  }> {
    return executeQuery(this);
  }
}

// Helper function to create a new query builder
export function resource<T extends ResoResource>(resource: T) {
  return new ResoQueryBuilder<T>(resource);
}
